import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import AuthShell from "@/components/AuthShell";
import { useAuth } from "@/lib/auth";
import { formatApiErrorDetail } from "@/lib/auth";

export default function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();
  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success("Welcome to Book Away.");
      const to = location.state?.from || "/pricing";
      navigate(to, { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Registration failed.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      subtitle="Foundation access"
      title="Register to view membership options."
      footerText="Already have an account?"
      footerHref="/login"
      footerLinkLabel="Log in"
      footerVariant="button"
    >
      <form data-testid="register-form" onSubmit={onSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="business_name" className="text-xs text-[#6B6B6B]">Business name</Label>
          <Input
            id="business_name"
            data-testid="register-business-name"
            value={form.business_name}
            onChange={(e) => update("business_name", e.target.value)}
            placeholder="Maison Noir"
            required
            className="border-black/10 bg-[#F7F5F2] text-[#2A2724] placeholder:text-[#9a9a9a] focus-visible:ring-gold"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact_name" className="text-xs text-[#6B6B6B]">Your name</Label>
          <Input
            id="contact_name"
            data-testid="register-contact-name"
            value={form.contact_name}
            onChange={(e) => update("contact_name", e.target.value)}
            placeholder="Alex Rivera"
            required
            className="border-black/10 bg-[#F7F5F2] text-[#2A2724] placeholder:text-[#9a9a9a] focus-visible:ring-gold"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-xs text-[#6B6B6B]">Work email</Label>
          <Input
            id="email"
            type="email"
            data-testid="register-email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@studio.com"
            required
            className="border-black/10 bg-[#F7F5F2] text-[#2A2724] placeholder:text-[#9a9a9a] focus-visible:ring-gold"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-xs text-[#6B6B6B]">Password</Label>
          <Input
            id="password"
            type="password"
            data-testid="register-password"
            value={form.password}
            onChange={(e) => update("password", e.target.value)}
            placeholder="At least 8 characters"
            minLength={8}
            required
            className="border-black/10 bg-[#F7F5F2] text-[#2A2724] placeholder:text-[#9a9a9a] focus-visible:ring-gold"
          />
        </div>

        {error && (
          <p data-testid="register-error" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <Button
          type="submit"
          data-testid="register-submit"
          disabled={loading}
          className="w-full rounded-full bg-gold py-6 text-sm font-medium text-black hover:bg-[#AA8627]"
        >
          {loading ? "Creating your account…" : "Create account & view pricing"}
        </Button>
      </form>
    </AuthShell>
  );
}
